// Экран «Ручное планирование» — шестнадцатый экран нового фронта (п.20, 2026-09-07).
// Таблица как в Google-таблице для быстрого ввода работ с раскаткой «вручную»:
// «Работа» + «Корпус» создают строку (новой работе заводится карточка с контуром
// «Ручной»), Enter — вниз, стрелки — по строкам, Esc — отмена, Ctrl+V в пустую
// «Работу» вставляет список из Excel (работа ⇥ длительность ⇥ предшественники).
// Предшественники задаются номерами задач: «12; 15НН+2; 7м».
import { useCallback, useEffect, useRef, useState } from 'react';
import { call, p, type Data } from '../api';
import { PoleSpiska } from '../Podskazki';
import type { ShellCtx } from '../shell';
import { fmt, kontury } from '../util';

type Tasks = Data<'/api/projects/{pid}/tasks', 'get'>;
type Task = Tasks['rows'][number];
type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type Floor = Data<'/api/projects/{pid}/structure', 'get'>[string][number];

/** строка таблицы: сохранённая задача либо пустая заготовка */
type RpRow = {
  isNew: boolean; busy?: boolean;
  id?: number; rev?: number; nomer?: number | null;
  rabota: string; korpus: string; etazh: string;
  dlit_r: string; dlit_avto?: number | null; pred: string;
  nachalo?: string | null; okonchanie?: string | null;
  mp?: Task['mp'];
};
const EMPTY_ROWS = 8;   // пустых строк снизу

const predText = (mp?: Task['mp']) => (mp || []).map(m =>
  m.nomer + (m.tip === 'НН' ? 'НН' : '') + (m.zapas ? (m.zapas > 0 ? '+' : '') + m.zapas : '') + (m.soft ? 'м' : '')
).join('; ');

export function Ruch({ ctx }: { ctx: ShellCtx }) {
  const [rows, setRows] = useState<RpRow[]>([]);
  const [works, setWorks] = useState<Work[]>([]);
  const [struct, setStruct] = useState<Record<string, Floor[]>>({});
  const [korpusa, setKorpusa] = useState<string[]>([]);
  const [chain, setChain] = useState(true);
  const [auto, setAuto] = useState(true);
  const [info, setInfo] = useState('');
  const timer = useRef<number | null>(null);
  const can = ctx.canPlan;
  const fail = (m: string) => ctx.onError?.(m);

  const isRuch = useCallback((name: string, list: Work[]) => {
    const w = list.find(x => x.name === name);
    if (!w) return false;
    return w.raskatka === 'вручную'
      || kontury(w.kontur).includes('Ручной');
  }, []);

  const pad = (list: RpRow[], lastKorpus: string): RpRow[] => {
    const out = list.slice();
    let n = out.filter(x => x.isNew).length;
    while (n < EMPTY_ROWS) {
      out.push({ isNew: true, rabota: '', korpus: lastKorpus, etazh: '', dlit_r: '', pred: '' });
      n++;
    }
    return out;
  };

  const load = useCallback(async () => {
    const [w, s, t] = await Promise.all([
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Record<string, Floor[]>>('get', p('/api/projects/{pid}/structure', { pid: ctx.pid })),
      call<Tasks>('get', p('/api/projects/{pid}/tasks', { pid: ctx.pid }) + '?korpus=*&order=name&limit=20000'),
    ]);
    if (!t.ok) { fail(t.error.message); return; }
    const wl = w.ok ? w.data : [];
    setWorks(wl);
    if (s.ok) setStruct(s.data);
    setKorpusa(t.data.korpusa);
    const keep = t.data.rows
      .filter(x => isRuch(x.rabota, wl)
        && (!ctx.matchesKorpus || ctx.matchesKorpus(String(x.korpus || '')))
        && (!ctx.matchesWork || ctx.matchesWork(x.rabota)))
      .sort((a, b) => (a.nomer ?? 1e12) - (b.nomer ?? 1e12))
      .map<RpRow>(x => ({
        isNew: false, id: x.id, rev: x.rev, nomer: x.nomer, rabota: x.rabota,
        korpus: String(x.korpus || ''), etazh: String(x.etazh ?? ''),
        dlit_r: x.dlit_r != null ? String(x.dlit_r) : '', dlit_avto: x.dlit_avto,
        pred: predText(x.mp), nachalo: x.nachalo, okonchanie: x.okonchanie, mp: x.mp,
      }));
    const lastK = [...keep].reverse().find(x => x.korpus)?.korpus || (ctx.korpus || t.data.korpusa[0] || '');
    setRows(pad(keep, lastK));
    setInfo('Строк: ' + keep.length + ' (работы с раскаткой «вручную»). Даты — по последней актуализации.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.korpus, ctx.filterKey, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  /** отложенный пересчёт после правки (галочка «пересчёт сразу») */
  const schedule = () => {
    if (!auto || !ctx.onRecalc) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => ctx.onRecalc?.(), 1200);
  };
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const focusCell = (i: number, f: string) => {
    window.setTimeout(() => {
      const el = document.querySelector<HTMLInputElement | HTMLSelectElement>(
        '#rpRows [data-r="' + i + '"][data-f="' + f + '"]');
      if (el) { el.focus(); if ('select' in el && typeof el.select === 'function') el.select(); }
    }, 20);
  };

  /** id работы: найти по имени или завести новую с контуром «Ручной» */
  async function workId(name: string, list: Work[]): Promise<number | null> {
    const found = list.find(x => x.name === name);
    if (found) return found.id;
    const r = await call<{ id: number }>('post', p('/api/projects/{pid}/works', { pid: ctx.pid }),
      { name, kontur: 'Ручной', raskatka: 'вручную' });
    if (!r.ok) { fail(r.error.message); return null; }
    const rw = await call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid }));
    if (rw.ok) setWorks(rw.data);
    return r.data.id;
  }

  /** создать задачу из заготовки: работа + корпус (+ этаж, длительность, предшественник) */
  async function create(i: number, cur: RpRow[]): Promise<RpRow[]> {
    const row = cur[i];
    if (!row || !row.isNew || row.busy || !row.rabota.trim() || !row.korpus) return cur;
    row.busy = true;
    const wid = await workId(row.rabota.trim(), works);
    row.busy = false;
    if (!wid) return cur;
    const r = await call<{ id: number; nomer: number }>('post',
      p('/api/projects/{pid}/tasks/single', { pid: ctx.pid }),
      { work_id: wid, korpus: row.korpus, etazh: row.etazh || null,
        dlit_ruchnaya: row.dlit_r.trim() || null });
    if (!r.ok) { fail(r.error.message); return cur; }
    ctx.onError?.('');
    const next = cur.slice();
    const saved: RpRow = { ...row, isNew: false, id: r.data.id, nomer: r.data.nomer, rev: 1, mp: [] };
    next[i] = saved;
    // предшественник: явный, иначе «цепочкой» — предыдущая сохранённая строка
    let pred = row.pred.trim();
    if (!pred && chain) {
      const prev = next.slice(0, i).reverse().find(x => !x.isNew && x.nomer != null);
      if (prev) pred = String(prev.nomer);
    }
    if (pred) {
      const rl = await call<{ rev: number; mp: Task['mp'] }>('patch',
        p('/api/tasks/{tid}', { tid: saved.id! }), { rev: saved.rev, pred_ruch: pred });
      if (rl.ok) { saved.rev = rl.data.rev; saved.mp = rl.data.mp; saved.pred = predText(rl.data.mp); }
      else { fail('Предшественник: ' + rl.error.message); saved.pred = ''; }
    }
    ctx.onSaved?.();
    schedule();
    const lastK = saved.korpus;
    focusCell(i + 1, 'rabota');
    return pad(next, lastK);
  }

  /** правка сохранённой строки */
  async function patch(i: number, body: Record<string, unknown>): Promise<boolean> {
    const row = rows[i];
    const r = await call<{ rev: number; mp?: Task['mp'] }>('patch',
      p('/api/tasks/{tid}', { tid: row.id! }), { rev: row.rev, ...body });
    if (!r.ok) { fail(r.error.message); if (r.error.code === 'stale') void load(); return false; }
    ctx.onError?.('');
    setRows(prev => {
      const next = prev.slice();
      const cur = { ...next[i], rev: r.data.rev };
      if (r.data.mp) { cur.mp = r.data.mp; cur.pred = predText(r.data.mp); }
      next[i] = cur;
      return next;
    });
    ctx.onSaved?.();
    schedule();
    return true;
  }

  async function remove(i: number) {
    const row = rows[i];
    if (!row.id) return;
    if (!confirm('Удалить задачу «' + row.rabota + ' · ' + row.korpus
      + (row.etazh ? ' · эт. ' + row.etazh : '') + '»?\nРучные связи с ней тоже удалятся.')) return;
    const r = await call('delete', p('/api/tasks/{tid}', { tid: row.id }));
    if (!r.ok) { fail(r.error.message); return; }
    ctx.onError?.(''); ctx.onSaved?.(); schedule();
    void load();
  }

  /** правка ячейки заготовки; заполнены работа и корпус — строка создаётся */
  function setNew(i: number, f: keyof RpRow, v: string) {
    setRows(prev => {
      const next = prev.slice();
      next[i] = { ...next[i], [f]: v } as RpRow;
      if (f === 'korpus') next[i].etazh = '';
      return next;
    });
  }
  async function maybeCreate(i: number) {
    setRows(prev => { void create(i, prev).then(setRows); return prev; });
  }

  /** вставка списка из Excel: строки — задачи, колонки — работа ⇥ длит ⇥ предш */
  async function onPaste(i: number, e: React.ClipboardEvent) {
    const text = e.clipboardData.getData('text');
    const lines = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    if (lines.length < 2 && !text.includes('\t')) return;   // обычная вставка слова
    e.preventDefault();
    let cur = rows.slice();
    let idx = i;
    for (const line of lines) {
      const parts = line.split('\t');
      while (idx >= cur.length || !cur[idx].isNew) idx++;
      if (idx >= cur.length) cur = pad(cur, cur[cur.length - 1]?.korpus || '');
      cur[idx] = {
        ...cur[idx], rabota: parts[0].trim(),
        dlit_r: (parts[1] || '').trim(), pred: (parts[2] || '').trim(),
        korpus: cur[idx].korpus || ctx.korpus || korpusa[0] || '',
      };
      cur = await create(idx, cur);
      idx++;
    }
    setRows(cur);
    void load();
  }

  function onKeyDown(e: React.KeyboardEvent, i: number, f: string) {
    const el = e.target as HTMLInputElement;
    if (e.key === 'Enter') { e.preventDefault(); el.blur(); focusCell(Math.min(i + 1, rows.length - 1), f); }
    else if (e.key === 'ArrowDown' && el.tagName === 'INPUT') { e.preventDefault(); el.blur(); focusCell(i + 1, f); }
    else if (e.key === 'ArrowUp' && el.tagName === 'INPUT') { e.preventDefault(); el.blur(); focusCell(i - 1, f); }
    else if (e.key === 'Escape') { el.blur(); void load(); }
  }

  return (
    <div>
      <div className="bar actbar"
        title="Пишите прямо в ячейки: «Работа» + «Корпус» создают строку. Enter — вниз, Tab — вправо, ↑↓ — по строкам, Esc — отмена. Ctrl+V в пустую «Работу» вставляет список из Excel. Предшественники — номерами: «12; 15НН+2»">
        <span className="grow" />
        <label title="Новая строка получает предшественником предыдущую строку">
          <input type="checkbox" style={{ width: 'auto' }} checked={chain}
            onChange={e => setChain(e.target.checked)} /> цепочкой
        </label>
        <label title="После каждого сохранения пересчитывать даты автоматически">
          <input type="checkbox" style={{ width: 'auto' }} checked={auto}
            onChange={e => setAuto(e.target.checked)} /> пересчёт сразу
        </label>
        {ctx.onRecalc && <button onClick={() => ctx.onRecalc?.()}>Пересчитать</button>}
      </div>
      <div className="tbl" style={{ maxHeight: '74vh' }}>
        <table id="rpTable">
          <thead>
            <tr>
              <th style={{ width: 56 }} title="№ задачи (ИД); по нему задаются предшественники; можно изменить">№</th>
              <th style={{ textAlign: 'left', minWidth: 260 }}>Работа</th>
              <th style={{ width: 90 }}>Корпус</th>
              <th style={{ width: 110 }}>Этаж</th>
              <th style={{ width: 64 }} title="Длительность, дней">Длит.</th>
              <th style={{ minWidth: 140 }} title="Предшественники номерами: 12; 15НН+2; 7м (м — мягкая связь)">Предш.</th>
              <th style={{ width: 84 }}>Начало</th>
              <th style={{ width: 84 }}>Оконч.</th>
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody id="rpRows">
            {rows.map((row, i) => row.isNew ? (
              <tr key={'n' + i} className="new">
                <td className="ro" />
                <td>
                  <PoleSpiska value={row.rabota} opts={works.map(w => w.name)}
                    placeholder="работа…" disabled={!can} dataR={i} dataF="rabota"
                    onChange={v => setNew(i, 'rabota', v)}
                    onPaste={e => void onPaste(i, e)}
                    onBlur={() => { if (row.rabota.trim() && row.korpus) void maybeCreate(i); }}
                    onKeyDown={e => onKeyDown(e, i, 'rabota')} />
                </td>
                <td>
                  <select data-r={i} data-f="korpus" value={row.korpus} disabled={!can}
                    onChange={e => { setNew(i, 'korpus', e.target.value); focusCell(i, 'etazh'); }}>
                    <option value="" />
                    {korpusa.map(k => <option key={k}>{k}</option>)}
                  </select>
                </td>
                <td>
                  <select data-r={i} data-f="etazh" value={row.etazh} disabled={!can}
                    onChange={e => { setNew(i, 'etazh', e.target.value);
                      if (row.rabota.trim() && row.korpus) void maybeCreate(i); }}>
                    <option value="">весь корпус, без этажа</option>
                    {(struct[row.korpus] || []).map(f => <option key={f.id}>{f.etazh}</option>)}
                  </select>
                </td>
                <td>
                  <input data-r={i} data-f="dlit_r" className="num" placeholder="дн" disabled={!can}
                    value={row.dlit_r}
                    onChange={e => setNew(i, 'dlit_r', e.target.value)}
                    onBlur={() => { if (row.rabota.trim() && row.korpus) void maybeCreate(i); }}
                    onKeyDown={e => onKeyDown(e, i, 'dlit_r')} />
                </td>
                <td>
                  <input data-r={i} data-f="pred" placeholder="№; №НН+2; №м" disabled={!can}
                    value={row.pred}
                    onChange={e => setNew(i, 'pred', e.target.value)}
                    onBlur={() => { if (row.rabota.trim() && row.korpus) void maybeCreate(i); }}
                    onKeyDown={e => onKeyDown(e, i, 'pred')} />
                </td>
                <td className="ro" /><td className="ro" /><td className="ro" />
              </tr>
            ) : (
              <tr key={row.id}>
                <td>
                  <input data-r={i} data-f="nomer" className="num" defaultValue={String(row.nomer ?? '')}
                    disabled={!can}
                    onBlur={async e => {
                      const v = e.target.value.trim();
                      if (v === String(row.nomer ?? '')) return;
                      if (!/^\d+$/.test(v)) { fail('№ — целое число'); e.target.value = String(row.nomer ?? ''); return; }
                      if (await patch(i, { nomer: +v })) void load(); else e.target.value = String(row.nomer ?? '');
                    }}
                    onKeyDown={e => onKeyDown(e, i, 'nomer')} />
                </td>
                <td className="ro name" title="Название и корпус после создания меняются в справочнике / модели">{row.rabota}</td>
                <td className="ro">{row.korpus}</td>
                <td className="ro">{row.etazh
                  || <span className="pw-muted">весь корпус</span>}</td>
                <td>
                  <input data-r={i} data-f="dlit_r" className="num" disabled={!can}
                    placeholder={row.dlit_avto != null ? String(row.dlit_avto) : ''}
                    defaultValue={row.dlit_r}
                    onBlur={async e => {
                      const v = e.target.value.trim();
                      if (v === row.dlit_r) return;
                      if (v !== '' && isNaN(+v.replace(',', '.'))) {
                        fail('Длительность — число дней'); e.target.value = row.dlit_r; return;
                      }
                      if (await patch(i, { dlit_ruchnaya: v || null })) {
                        setRows(prev => { const n = prev.slice(); n[i] = { ...n[i], dlit_r: v }; return n; });
                      } else e.target.value = row.dlit_r;
                    }}
                    onKeyDown={e => onKeyDown(e, i, 'dlit_r')} />
                </td>
                <td>
                  <input data-r={i} data-f="pred" defaultValue={row.pred} disabled={!can}
                    onBlur={async e => {
                      if (e.target.value === row.pred) return;
                      const ok = await patch(i, { pred_ruch: e.target.value });
                      if (!ok) e.target.value = row.pred;
                    }}
                    onKeyDown={e => onKeyDown(e, i, 'pred')} />
                </td>
                <td className="ro">{fmt(row.nachalo)}</td>
                <td className="ro">{fmt(row.okonchanie)}</td>
                <td className="ro">
                  {can && <a className="del" title="удалить задачу" onClick={() => void remove(i)}>×</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="muted" style={{ marginTop: 6 }}>{info}</div>
    </div>
  );
}
